import React, { MouseEvent, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PageStoreExtractor, UrlDetailsExtractor } from '../../context/StoreContext';
import { Component, ComponentProps } from '../../types/common';
import {
	processComponentStylePseudoClasses,
	processStyleObjectToCSS,
} from '../../util/styleProcessor';
import { HelperComponent } from '../HelperComponents/HelperComponent';
import { SubHelperComponent } from '../HelperComponents/SubHelperComponent';
import { getHref } from '../util/getHref';
import { getTranslations } from '../util/getTranslations';
import { isBrowserHandledLinkClick } from '../util/isBrowserHandledLinkClick';
import { navigateInApp } from '../util/navigateInApp';
import { runEvent } from '../util/runEvent';
import useDefinition from '../util/useDefinition';
import MenuStyle from './MenuStyle';
import { propertiesDefinition, stylePropertiesDefinition } from './menuProperties';
import Children from '../Children';
import { styleProperties, styleDefaults, stylePropertiesForTheme } from './menuStyleProperties';
import { IconHelper } from '../util/IconHelper';
import getSrcUrl from '../util/getSrcUrl';
import { findPropertyDefinitions } from '../util/lazyStylePropertyUtil';

function Menu(props: Readonly<ComponentProps>) {
	const location = useLocation();
	const {
		pageDefinition: { translations },
		definition,
		locationHistory,
		context,
	} = props;
	const pageExtractor = PageStoreExtractor.getForContext(context.pageName);
	const urlExtractor = UrlDetailsExtractor.getForContext(context.pageName);
	const {
		key,
		properties: {
			linkPath,
			label,
			target,
			features,
			showButton,
			externalButtonTarget,
			externalButtonFeatures,
			caretIconClose,
			caretIconOpen,
			icon,
			imageIcon,
			activeImageIcon,
			isMenuOpen,
			onlyIconMenu,
			onMenuClose,
			onMenuOpen,
			onClick,
			pathsActiveFor,
			designType: menuDesignSelectionType,
			colorScheme: menuColorScheme,
			subMenuOrientation,
			readOnly,
			showOpenCloseButton,
			analyticsLabel,
		} = {},
		stylePropertiesWithPseudoStates,
	} = useDefinition(
		definition,
		propertiesDefinition,
		stylePropertiesDefinition,
		locationHistory,
		pageExtractor,
		urlExtractor,
	);
	const resolvedLink = getHref(linkPath, location);
	const [isMenuOpenState, setIsMenuOpenState] = React.useState(isMenuOpen);
	const [isMenuActive, setIsMenuActive] = React.useState(false);
	const { pathname } = useLocation();
	const [containerHover, setContainerHover] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	React.useEffect(() => {
		if (!pathsActiveFor?.length) return;
		const paths = pathsActiveFor.split(',');
		let hasPath;
		if (pathname === '/') {
			hasPath = !!paths.find((e: string) => pathname === e);
		} else {
			const lowerPath = pathname.toLowerCase();
			hasPath = !!paths
				.filter((e: string) => e !== '/')
				.find((e: string) => {
					const raw = e.trim().toLowerCase();
					if (!raw) return false;
					// A trailing `$` anchors to a whole path segment: `ai$` lights up
					// on /ai and /ai/something, but NOT on /aiStudio.
					//
					// Opt-in rather than the new default on purpose. The plain match
					// below is a bare substring, and around 120 menus across these apps
					// are configured against it — `asset` is relied on to catch
					// /assets, `campaignProductMap` to catch /campaignProductMapping.
					// Making anchoring automatic would quietly unlight all of those, so
					// the only menus that change are the ones that ask to.
					// The segment can sit anywhere: on an app's own domain the path is
					// /ads, but under /<app>/<client>/page/ads it is the last segment.
					// Comparing the whole path only ever worked on the first.
					if (raw.endsWith('$')) {
						const seg = `/${raw.slice(0, -1)}`;
						return lowerPath.endsWith(seg) || lowerPath.indexOf(`${seg}/`) >= 0;
					}
					return lowerPath.indexOf(`/${raw}`) >= 0;
				});
		}
		setIsMenuActive(hasPath);
	}, [pathname, pathsActiveFor]);

	const hoverStyle = processComponentStylePseudoClasses(
		props.pageDefinition,
		{ hover: true },
		stylePropertiesWithPseudoStates,
	);

	const visitedStyle = processComponentStylePseudoClasses(
		props.pageDefinition,
		{ visited: true },
		stylePropertiesWithPseudoStates,
	);

	const regularStyle = processComponentStylePseudoClasses(
		props.pageDefinition,
		{ visited: false, hover: false },
		stylePropertiesWithPseudoStates,
	);

	const externalButton = showButton ? (
		<i
			className="_externalButton fa fa-solid fa-up-right-from-square"
			onClick={
				readOnly
					? undefined
					: e => {
							e.stopPropagation();
							e.preventDefault();
							if (externalButtonTarget === '_self') {
								navigateInApp(resolvedLink);
							} else {
								window.open(
									resolvedLink,
									externalButtonTarget,
									externalButtonFeatures ?? features,
								);
							}
						}
			}
		>
			<SubHelperComponent definition={definition} subComponentName="externalIcon" />
		</i>
	) : (
		<></>
	);

	const leftIconButton = icon ? (
		<i className={`_icon ${icon}`}>
			<SubHelperComponent definition={definition} subComponentName="icon" />
		</i>
	) : imageIcon && !activeImageIcon ? (
		<>
			<img className={`_imageIcon ${imageIcon}`} src={getSrcUrl(imageIcon)} alt="imageIcon" />
			<SubHelperComponent definition={definition} subComponentName="imageIcon" />
		</>
	) : !imageIcon && activeImageIcon ? (
		<>
			<img
				className={`_activeImageIcon ${activeImageIcon}`}
				src={getSrcUrl(activeImageIcon)}
				alt="activeImageIcon"
			/>
			<SubHelperComponent definition={definition} subComponentName="activeImageIcon" />
		</>
	) : imageIcon && activeImageIcon && (isHovered || isMenuActive) ? (
		<>
			<img
				className={`_activeImageIcon ${activeImageIcon}`}
				src={getSrcUrl(activeImageIcon)}
				alt="activeImageIcon"
			/>
			<SubHelperComponent definition={definition} subComponentName="activeImageIcon" />
		</>
	) : imageIcon && activeImageIcon && (!isHovered || !isMenuActive) ? (
		<>
			<img className={`_imageIcon ${imageIcon}`} src={getSrcUrl(imageIcon)} alt="imageIcon" />
			<SubHelperComponent definition={definition} subComponentName="imageIcon" />
		</>
	) : (
		<></>
	);

	const menuToggle = (e: MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		e.preventDefault();
		if (readOnly) return;
		const func =
			props.pageDefinition?.eventFunctions?.[isMenuOpenState ? onMenuClose : onMenuOpen];
		if (func) {
			(async () =>
				await runEvent(
					func,
					key,
					context.pageName,
					props.locationHistory,
					props.pageDefinition,
				))();
		}
		setIsMenuOpenState(!isMenuOpenState);
	};

	const caretIcon =
		showOpenCloseButton &&
		Object.entries(definition?.children ?? {}).filter(e => e[1]).length ? (
			<div className="_caretIconContainer">
				<i
					className={`_caretIcon ${isMenuOpenState ? caretIconOpen : caretIconClose}`}
					onClick={menuToggle}
				>
					<SubHelperComponent definition={definition} subComponentName="caretIcon" />
				</i>
			</div>
		) : (
			<></>
		);

	const styleKey = `${key}_${
		locationHistory?.length ? locationHistory.map(e => e.index).join('_') : ''
	}`;

	const styleComp = (
		<style key={`${styleKey}_style`}>
			{/* One string: React's server renderer emits nothing for a <style> with several
			    children, which breaks hydration (ui-app/ssr/HYDRATION_PLAN.md). */}
			{[
				processStyleObjectToCSS(
					regularStyle?.comp,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}`,
				),
				processStyleObjectToCSS(
					visitedStyle?.comp,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}:visited`,
				),
				processStyleObjectToCSS(
					hoverStyle?.comp,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}:hover, .comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}._isActive`,
				),
				processStyleObjectToCSS(
					regularStyle?.externalIcon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme} > ._externalButton`,
				),
				processStyleObjectToCSS(
					visitedStyle?.externalIcon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}:visited > ._externalButton`,
				),
				processStyleObjectToCSS(
					hoverStyle?.externalIcon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}:hover > ._externalButton, .comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}._isActive > ._externalButton`,
				),
				processStyleObjectToCSS(
					regularStyle?.icon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme} > ._icon`,
				),
				processStyleObjectToCSS(
					visitedStyle?.icon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}:visited > ._icon`,
				),
				processStyleObjectToCSS(
					hoverStyle?.icon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}:hover > ._icon, .comp.compMenu._${styleKey}menu_css._isActive.${menuDesignSelectionType}.${menuColorScheme} > ._icon`,
				),
				processStyleObjectToCSS(
					regularStyle?.imageIcon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme} > ._imageIcon`,
				),
				processStyleObjectToCSS(
					visitedStyle?.imageIcon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}:visited > ._imageIcon`,
				),
				processStyleObjectToCSS(
					hoverStyle?.imageIcon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}:hover > ._imageIcon, .comp.compMenu._${styleKey}menu_css._isActive.${menuDesignSelectionType}.${menuColorScheme} > ._imageIcon`,
				),
				processStyleObjectToCSS(
					regularStyle?.activeImageIcon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme} > ._activeImageIcon`,
				),
				processStyleObjectToCSS(
					visitedStyle?.activeImageIcon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}:visited > ._activeImageIcon`,
				),
				processStyleObjectToCSS(
					hoverStyle?.activeImageIcon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}:hover > ._activeImageIcon, .comp.compMenu._${styleKey}menu_css._isActive.${menuDesignSelectionType}.${menuColorScheme} > ._activeImageIcon`,
				),
				processStyleObjectToCSS(
					regularStyle?.caretIcon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme} > ._caretIcon`,
				),
				processStyleObjectToCSS(
					visitedStyle?.caretIcon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}:visited > ._caretIcon`,
				),
				processStyleObjectToCSS(
					hoverStyle?.caretIcon,
					`.comp.compMenu._${styleKey}menu_css.${menuDesignSelectionType}.${menuColorScheme}:hover > ._caretIcon, .comp.compMenu._${styleKey}menu_css._isActive.${menuDesignSelectionType}.${menuColorScheme} > ._caretIcon`,
				),
			].join('')}
		</style>
	);

	const children =
		definition.children && isMenuOpenState ? (
			<div
				className={`${subMenuOrientation}`}
				style={(containerHover ? hoverStyle : regularStyle)?.subMenuContainer}
				onMouseOver={() => setContainerHover(true)}
				onMouseLeave={() => setContainerHover(false)}
			>
				<SubHelperComponent
					definition={props.definition}
					subComponentName="subMenuContainer"
				/>
				<Children
					pageDefinition={props.pageDefinition}
					renderableChildren={definition.children}
					context={{ ...context, menuLevel: (context.menuLevel ?? 0) + 1 }}
					locationHistory={locationHistory}
				/>
			</div>
		) : (
			<></>
		);

	return (
		<>
			{styleComp}
			<a
				className={`comp compMenu _${styleKey}menu_css ${menuDesignSelectionType} ${menuColorScheme} ${
					isMenuActive ? '_isActive' : ''
				} ${readOnly ? '_disabled' : ''} _level${context.menuLevel ?? 0}`}
				href={readOnly ? 'javascript:void(0)' : resolvedLink}
				target={target}
				data-analytics-label={analyticsLabel || undefined}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				onClick={e => {
					// Cmd/Ctrl, Shift or Alt click on a real link is the browser's gesture to open
					// it in a new tab or window. Every branch below hijacks the anchor with a
					// preventDefault, so hand the event back untouched and leave this page alone:
					// the click was never meant to act here.
					if (linkPath && isBrowserHandledLinkClick(e)) return;

					if ((!target || target === '_self') && linkPath) {
						e.stopPropagation();
						e.preventDefault();
						navigateInApp(resolvedLink);
					} else if (features && linkPath) {
						e.stopPropagation();
						e.preventDefault();
						window.open(resolvedLink, target, features);
					} else if (!onClick && !linkPath) {
						// `!linkPath` matters: menuToggle preventDefaults, so without it a
						// link with an explicit target and no `features` -- target="_blank"
						// being the ordinary way to say "open this in a new tab" -- landed
						// here and had its navigation cancelled. It looked like a dead menu
						// item. Nothing above claims that case, and nothing needs to: the
						// anchor already carries both href and target, so the right move is
						// to leave the click alone and let the browser open the tab.
						menuToggle(e);
					}

					// A menu with no linkPath is an action, not a link, and must not navigate.
					// getHref defaults an absent path to '' and does not special-case it, so it
					// resolves to the current page: without this the browser follows the anchor and
					// the page reloads, which looks like the onClick did nothing even though it ran.
					if (!linkPath) e.preventDefault();

					const func = onClick
						? props.pageDefinition?.eventFunctions?.[onClick]
						: undefined;
					if (!func) return;
					(async () =>
						await runEvent(
							func,
							key,
							context.pageName,
							props.locationHistory,
							props.pageDefinition,
						))();
				}}
			>
				<HelperComponent context={props.context} definition={definition} />
				{leftIconButton}
				{onlyIconMenu ? '' : getTranslations(label, translations)}
				{externalButton}
				{caretIcon}
			</a>
			{children}
		</>
	);
}

const { designType, colorScheme } = findPropertyDefinitions(
	propertiesDefinition,
	'designType',
	'colorScheme',
);

const component: Component = {
	order: 22,
	name: 'Menu',
	displayName: 'Menu',
	description: 'Menu component',
	component: Menu,
	styleComponent: MenuStyle,
	styleDefaults: styleDefaults,
	propertyValidation: (): Array<string> => [],
	properties: propertiesDefinition,
	styleProperties: stylePropertiesDefinition,
	stylePseudoStates: ['hover', 'disabled', 'active', 'visited'],
	allowedChildrenType: new Map([
		['Menu', -1],
		['Grid', 1],
	]),
	defaultTemplate: {
		key: '',
		type: 'Menu',
		name: 'Menu',
		properties: {
			label: { value: 'Menu' },
			icon: { value: 'fa-solid fa-bars' },
		},
	},
	sections: [
		{ name: 'Horizontal Menu', pageName: 'horizontalMenu' },
		{ name: 'Vertical Menu', pageName: 'verticalMenu' },
	],
	propertiesForTheme: [designType, colorScheme],
	stylePropertiesForTheme: stylePropertiesForTheme,
	externalStylePropsForThemeJson: true,
};

export default component;
